// friendsService.js
// Pick Up Draft — Phase B1 (Friends).
//
// Web mirror of FirebaseRepository_Friends.swift, field-for-field. The friends system is a
// prerequisite for the friend-challenge game-creation path (plan §4b/§5). It's also the half
// of the feature that's fully verifiable cross-device on its own: send a request from web,
// accept it on iOS, and watch the reciprocal /friends entry appear on both sides.
//
// Firestore structure (owned here):
//   usernames/{USERNAME}                 → { uid, email, createdAt }   (public read; search)
//   friend_requests/{auto-id}            → { fromUID, fromUsername, toUID, toUsername,
//                                            status: "pending"|"accepted"|"declined", createdAt }
//   users/{uid}/friends/{friendUID}      → { username, since }
//
// What the client writes vs. what the server writes (see plan §5):
//   • Accept = a client BATCH: update the request status → "accepted" AND write the
//     accepter's own /friends entry. The onFriendRequestAccepted Cloud Function then writes
//     the *requester's* reciprocal /friends entry (the half the client can't write).
//   • Remove = delete own /friends entry; onFriendRemoved CF removes the reciprocal.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

// ─── Search ─────────────────────────────────────────────────────────────────
// Exact-match only: username documents are keyed by the UPPERCASED username, so a single
// getDoc is sufficient (mirrors searchUsers — no full-text scan). Returns null if there's
// no match, or if the only match is the searcher themselves.
export async function searchUser(rawQuery, excludingUID) {
  const key = (rawQuery || "").toUpperCase().trim();
  if (!key) return null;

  const snap = await getDoc(doc(db, "usernames", key));
  if (!snap.exists()) return null;

  const uid = snap.data()?.uid;
  if (!uid || uid === excludingUID) return null;

  return { id: uid, username: snap.id }; // doc ID is already uppercase by design
}

// ─── Send request ─────────────────────────────────────────────────────────────
// Guards against a duplicate pending request first (same fromUID → toUID), then creates the
// pending doc. The six keys exactly match what the security rules require on create.
// Returns true if a request was created, false if one was already pending (no-op, like iOS).
export async function sendFriendRequest({ fromUID, fromUsername, toUID, toUsername }) {
  const existing = await getDocs(
    query(
      collection(db, "friend_requests"),
      where("fromUID", "==", fromUID),
      where("toUID", "==", toUID),
      where("status", "==", "pending")
    )
  );
  if (!existing.empty) return false;

  await addDoc(collection(db, "friend_requests"), {
    fromUID,
    fromUsername,
    toUID,
    toUsername,
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return true;
}

// ─── Accept request (client batch) ──────────────────────────────────────────
// 1. request.status → "accepted"
// 2. write the accepter's (toUID) own /friends/{fromUID} entry — owner-authorized.
// The requester's reciprocal entry is written by onFriendRequestAccepted (admin).
export async function acceptFriendRequest({ requestID, fromUID, fromUsername, toUID }) {
  const batch = writeBatch(db);

  batch.update(doc(db, "friend_requests", requestID), { status: "accepted" });

  batch.set(doc(db, "users", toUID, "friends", fromUID), {
    username: fromUsername,
    since: serverTimestamp(),
  });

  await batch.commit();
}

// ─── Decline request ──────────────────────────────────────────────────────────
// Kept (not deleted) for audit + to stop immediate re-sends, exactly like iOS.
export async function declineFriendRequest(requestID) {
  await updateDoc(doc(db, "friend_requests", requestID), { status: "declined" });
}

// ─── Remove friend ──────────────────────────────────────────────────────────
// Deletes own /friends entry; onFriendRemoved CF removes the reciprocal.
export async function removeFriend({ userID, friendUID }) {
  await deleteDoc(doc(db, "users", userID, "friends", friendUID));
}

// ─── Real-time listeners ──────────────────────────────────────────────────────
// Each returns the onSnapshot unsubscribe function — call it on unmount (plan §8).

// users/{uid}/friends → [{ id: friendUID, username }]
export function listenToFriends(userID, onChange) {
  return onSnapshot(
    collection(db, "users", userID, "friends"),
    (snap) => {
      const entries = snap.docs
        .map((d) => {
          const username = d.data()?.username;
          return typeof username === "string" ? { id: d.id, username } : null;
        })
        .filter((e) => e !== null);
      onChange(entries);
    },
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("friendsService: friends listener error", err);
      }
    }
  );
}

// friend_requests where toUID == me & status == pending
// → [{ id, fromUID, fromUsername, toUID, toUsername }]
export function listenToIncomingRequests(userID, onChange) {
  return onSnapshot(
    query(
      collection(db, "friend_requests"),
      where("toUID", "==", userID),
      where("status", "==", "pending")
    ),
    (snap) => {
      const requests = snap.docs
        .map((d) => {
          const r = d.data();
          if (
            typeof r.fromUID !== "string" ||
            typeof r.fromUsername !== "string" ||
            typeof r.toUID !== "string" ||
            typeof r.toUsername !== "string"
          ) {
            return null;
          }
          return {
            id: d.id,
            fromUID: r.fromUID,
            fromUsername: r.fromUsername,
            toUID: r.toUID,
            toUsername: r.toUsername,
          };
        })
        .filter((r) => r !== null);
      onChange(requests);
    },
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("friendsService: incoming requests listener error", err);
      }
    }
  );
}

// friend_requests where fromUID == me & status == pending → Set(toUID)
// (so the UI can mark a searched user as "request sent").
export function listenToSentRequests(userID, onChange) {
  return onSnapshot(
    query(
      collection(db, "friend_requests"),
      where("fromUID", "==", userID),
      where("status", "==", "pending")
    ),
    (snap) => {
      const uids = new Set(
        snap.docs.map((d) => d.data()?.toUID).filter((u) => typeof u === "string")
      );
      onChange(uids);
    },
    (err) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("friendsService: sent requests listener error", err);
      }
    }
  );
}
